import { useIdentityContext, useUserData } from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isLoggedIn, isLoading } = useIdentityContext();
  const { userData, isLoading: userDataLoading, whoamiData } = useUserData();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the actual query state to know if it's still loading
  const apiClient = useReactQuery();
  const whoamiQuery = apiClient.useQuery(
    'get',
    '/v1/whoami',
    {},
    {
      enabled: isLoggedIn,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  useEffect(() => {
    // If we're on profile setup page, allow it (don't redirect away from it)
    if (location.pathname === '/profile/setup') {
      return;
    }

    // If not logged in, redirect to signin-v2 (but allow home page for LandingPage)
    if (!isLoggedIn && !isLoading && location.pathname !== '/') {
      navigate('/signin-v2');
      return;
    }

    // Only redirect to profile setup if we have fresh whoami data and user is explicitly null
    // This means the whoami API call completed and returned user: null
    // This check should run even on the home page for logged-in users
    if (
      isLoggedIn &&
      !whoamiQuery.isLoading &&
      whoamiQuery.data &&
      whoamiQuery.data.user === null
    ) {
      navigate('/profile/setup');
      return;
    }

    // If logged in and has user profile, allow access to protected routes
    if (isLoggedIn && userData) {
      return;
    }

    // If we're logged in but don't have definitive data yet, allow access
    // This prevents premature redirects while data is loading or after profile creation
    if (isLoggedIn && whoamiQuery.isLoading) {
      return;
    }

    // Allow home page for unauthenticated users (HomePage will show LandingPage)
    if (location.pathname === '/' && !isLoggedIn) {
      return;
    }
  }, [
    isLoggedIn,
    userData,
    whoamiData,
    isLoading,
    userDataLoading,
    whoamiQuery.isLoading,
    whoamiQuery.data,
    navigate,
    location.pathname,
  ]);

  // Don't block the page with loading screen - let the NavBar handle its own loading state
  return <>{children}</>;
};

export default AuthGuard;
